import { useState } from 'react';
import { useAssetUsage, useAssetUsageSummary, useShopLaborTotals } from '@/hooks/useAssetUsage';
import { usePermissions } from '@/hooks/usePermissions';
import { AttributionDisclaimer } from './AttributionDisclaimer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { Clock, TrendingUp, Wrench, Activity } from 'lucide-react';

interface AssetUsageTabProps {
  assetId: string;
}

type DateRangeOption = '7d' | '30d' | 'mtd' | 'all';

export function AssetUsageTab({ assetId }: AssetUsageTabProps) {
  const [rangeOption, setRangeOption] = useState<DateRangeOption>('30d');
  const { isManager } = usePermissions();

  const getDateRange = () => {
    const now = new Date();
    switch (rangeOption) {
      case '7d':
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case '30d':
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      case 'mtd':
        return { start: startOfMonth(now), end: endOfDay(now) };
      default:
        return undefined;
    }
  };

  const dateRange = getDateRange();
  const { data: events, isLoading: loadingEvents } = useAssetUsage(assetId, dateRange);
  const { summary, isLoading: loadingSummary } = useAssetUsageSummary(assetId, dateRange);
  
  // Get shop totals for comparison (Manager only)
  const { data: shopTotals, isLoading: loadingShopTotals } = useShopLaborTotals(
    dateRange ?? { start: startOfDay(subDays(new Date(), 30)), end: endOfDay(new Date()) }
  );

  const formatHours = (minutes: number) => {
    const hours = minutes / 60;
    return hours.toFixed(1);
  };

  // Calculate efficiency
  const efficiency = summary.totalActualMinutes > 0 
    ? (summary.totalBookMinutes / summary.totalActualMinutes * 100).toFixed(0)
    : null;

  if (loadingEvents || loadingSummary) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Usage Analytics</h3>
        <Select value={rangeOption} onValueChange={(v) => setRangeOption(v as DateRangeOption)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="mtd">Month to Date</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Clock className="h-4 w-4" />
              Book Hours
            </div>
            <p className="text-2xl font-bold mt-1">{formatHours(summary.totalBookMinutes)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Wrench className="h-4 w-4" />
              Actual Hours
            </div>
            <p className="text-2xl font-bold mt-1">{formatHours(summary.totalActualMinutes)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Activity className="h-4 w-4" />
              Usage Events
            </div>
            <p className="text-2xl font-bold mt-1">{summary.usageCount}</p>
          </CardContent>
        </Card>

        {efficiency && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <TrendingUp className="h-4 w-4" />
                Efficiency
              </div>
              <p className="text-2xl font-bold mt-1">{efficiency}%</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Attribution Breakdown */}
      {(summary.bayEnabledMinutes > 0 || summary.jobAttributedMinutes > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attribution Breakdown</CardTitle>
            <CardDescription>How labor hours are attributed to this asset</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm">Bay-Enabled (Non-overlapping)</span>
              </div>
              <span className="font-mono">{formatHours(summary.bayEnabledMinutes)} hrs</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm">Job-Attributed (May overlap)</span>
              </div>
              <span className="font-mono">{formatHours(summary.jobAttributedMinutes)} hrs</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shop Comparison - Manager Only */}
      {isManager && shopTotals && summary.bayEnabledMinutes > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shop Comparison</CardTitle>
            <CardDescription>This asset's contribution to shop totals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">% of Shop Book Hours</span>
              <span className="font-mono font-medium">
                {shopTotals.total_book_hours > 0 
                  ? ((summary.bayEnabledMinutes / 60 / shopTotals.total_book_hours) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disclaimer */}
      {summary.jobAttributedMinutes > 0 && (
        <AttributionDisclaimer />
      )}

      {/* Recent Usage Events */}
      {(events ?? []).length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium">Recent Activity</h3>
          {(events ?? []).slice(0, 10).map(event => (
            <Card key={event.id}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={event.value_kind === 'bay_enabled' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {event.value_kind === 'bay_enabled' ? 'Bay' : 'Job'}
                      </Badge>
                      {event.technician && (
                        <span className="text-sm">{event.technician.name}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(event.created_at), 'MMM d, h:mm a')}
                      {event.ticket_id && ` • Ticket ${event.ticket_id.slice(-6)}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm">{formatHours(event.book_minutes)} hrs</p>
                    <p className="text-xs text-muted-foreground">
                      {event.source === 'derived_bay' ? 'Auto (bay)' : 
                       event.source === 'derived_job' ? 'Auto (job)' : 'Manual'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(events ?? []).length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Activity className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No usage recorded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Usage is automatically tracked when tickets are completed
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
